import { memo, useCallback, useMemo, useState } from "react";
import {
  formatNumber,
  LoadingSkeleton,
  Popover,
  Tabs,
  useBreakpoints
} from "@narmi/design_system";
import { format } from "date-fns";
import cc from "classcat";
import { Localized, useLocalization } from "@fluent/react";
import { useCurrentUser } from "../../contexts/CurrentUserContext";
import type { SettledTransactionsState } from "../types";
import BaseChart from "./BaseChart";
import {
  isDemoStaticData,
  useBalanceChartHiddenAccountsSync,
  useChartStaticData,
  useChartData,
  useHasBalanceOwedAccounts
} from "./hooks";
import { useBalanceChartStorage } from "./storage";
import styles from "./BalanceChart.module.scss";
import { CHART_TYPES, TIME_PERIODS } from "./constants";
import type { ChartType, TimePeriod } from "./types";
import DemoChartOverlay from "./DemoChartOverlay/DemoChartOverlay";

const HeadLine = memo(
  ({ isLoading, balance }: { isLoading: boolean; balance: number | null }) => {
    if (isLoading) {
      return (
        <span className={styles.amountLoading}>
          <LoadingSkeleton isLoading lines={1} />
        </span>
      );
    }

    if (balance !== null) {
      return formatNumber(balance, "currency");
    }

    return "--";
  }
);

type BalanceChartProps = {
  settledTransactionsState: SettledTransactionsState;
};

const BalanceChart = ({ settledTransactionsState }: BalanceChartProps) => {
  const { l10n } = useLocalization();
  const [isSelectingChartType, setIsSelectingChartType] = useState(false);
  const { currentUser } = useCurrentUser();
  const userId = currentUser?.id?.toString() || "";
  const { m } = useBreakpoints();
  const isSmallViewport = !m;
  const hasBalanceOwedAccounts = useHasBalanceOwedAccounts();
  useBalanceChartHiddenAccountsSync();
  const { isLoading: areTransactionsLoading, settledTransactions } =
    settledTransactionsState;
  const hasSettledTransactions = settledTransactions.length > 0;
  const [storedPreferences, setStoredPreferences] =
    useBalanceChartStorage(userId);

  const {
    staticData,
    isResolving: isStaticDataLoading,
    latestBalance: reportBalance,
    error: reportError
  } = useChartStaticData(hasSettledTransactions, areTransactionsLoading);
  const isDemo = isDemoStaticData(staticData);

  const {
    chartType,
    setChartType: setStoredChartType,
    chartData,
    windowStart,
    windowEnd,
    availableTimePeriods,
    effectiveTimePeriod,
    latestBalance,
    periodChange,
    isLoading,
    error
  } = useChartData(
    settledTransactionsState,
    hasBalanceOwedAccounts ? storedPreferences.chartType : "balances",
    hasBalanceOwedAccounts,
    isSmallViewport,
    staticData
  );
const visibleTimePeriods = useMemo(
  () => TIME_PERIODS.filter((timePeriod) => availableTimePeriods.includes(timePeriod)),
  [availableTimePeriods]
);

  const handleTabChange = useCallback(
    (index: number) => {
      // Demo data only exists for the monthly view, so the tab is locked.
      if (isDemo) return;

      const timePeriod = visibleTimePeriods[index];
      if (!timePeriod) return;

      setStoredPreferences?.((prev) => ({
        ...prev,
        timePeriod
      }));
    },
    [isDemo, setStoredPreferences, visibleTimePeriods]
  );

const selectedTab = Math.max(0, visibleTimePeriods.indexOf(effectiveTimePeriod));

  const areTabsDisplayed = !isDemo && !isSmallViewport;

  const setChartType = useCallback(
    (type: ChartType) => {
      setStoredChartType(type);
      setStoredPreferences?.((prev) => ({
        ...prev,
        chartType: type
      }));
    },
    [setStoredChartType, setStoredPreferences]
  );
  const isHeadlineLoading = isStaticDataLoading || (isLoading && !isDemo);
  const headlineBalance =
    chartType === "balances" && staticData !== "empty"
      ? reportBalance
      : latestBalance;
  const hasHeadlineError =
    chartType === "balances" ? Boolean(reportError) : Boolean(error);

  const amountContent: React.ReactNode = (
    <HeadLine isLoading={isHeadlineLoading} balance={headlineBalance} />
  );

  // Kept as literal FTL ids so the localization tooling can find them.
  const timePeriodTabLabel = (timePeriod: TimePeriod) => {
    switch (timePeriod) {
      case "year":
        return l10n.getString("dashboard-balance-chart-tab-yearly");
      case "quarter":
        return l10n.getString("dashboard-balance-chart-tab-quarterly");
      default:
        return l10n.getString("dashboard-balance-chart-tab-monthly");
    }
  };

  const chartTypeLabel = (type: ChartType) =>
    type === "balances"
      ? l10n.getString("dashboard-balance-chart-title")
      : l10n.getString("dashboard-balance-chart-title-owed");
  const availableChartTypes = hasBalanceOwedAccounts
    ? CHART_TYPES
    : (["balances"] as const);
  const isMoreThanOneChartType = availableChartTypes.length > 1;
  const chartTypeTitle = (
    <span
      className={cc([
        styles.titleRow,
        isMoreThanOneChartType && styles.titleRowTrigger
      ])}
    >
      <h4 className={styles.title}>{chartTypeLabel(chartType)}</h4>
      {isMoreThanOneChartType && (
        <span
          className={cc([
            "narmi-icon-chevron-down",
            styles.trendIcon,
            isSelectingChartType && styles.trendIconOpen
          ])}
        />
      )}
    </span>
  );

  const isPeriodChangeDisplayed = useMemo(
    () => !isSmallViewport && !isDemo,
    [isSmallViewport, isDemo]
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <div
            className={cc([
              styles.summaryContent,
              { [styles["summaryContent--loading"]]: isLoading }
            ])}
          >
            <div>
              {availableChartTypes.length > 1 ? (
                <Popover
                  isOpen={isSelectingChartType}
                  onUserEnable={() => !isDemo && setIsSelectingChartType(true)}
                  onUserDismiss={() => setIsSelectingChartType(false)}
                  content={
                    <div className={styles.chartTypeMenu} role="menu">
                      <span className={styles.chartTypeMenuHeading}>
                        {l10n.getString("dashboard-balance-chart-type-heading")}
                      </span>
                      {availableChartTypes.map((type) => (
                        <button
                          key={type}
                          type="button"
                          role="menuitemradio"
                          aria-checked={type === chartType}
                          className={styles.chartTypeMenuItem}
                          onClick={() => {
                            setChartType(type);
                            setIsSelectingChartType(false);
                          }}
                        >
                          <span>{chartTypeLabel(type)}</span>
                          {type === chartType && (
                            <span
                              aria-hidden="true"
                              className={cc([
                                "narmi-icon-check",
                                styles.chartTypeMenuCheck
                              ])}
                            />
                          )}
                        </button>
                      ))}
                    </div>
                  }
                >
                  {/* `Popover` renders this inside its own `role="button"`
                      element, so it must not be interactive itself. */}
                  {chartTypeTitle}
                </Popover>
              ) : (
                chartTypeTitle
              )}
            </div>
            <div className={styles.row}>
              <span className={styles.amount}>{amountContent}</span>
              {isPeriodChangeDisplayed &&
                !hasHeadlineError &&
                periodChange &&
                periodChange.percentage !== null && (
                  <div
                    className={cc([
                      styles.periodChange,
                      periodChange.isPositive && styles.periodChangePositive
                    ])}
                  >
                    <span
                      className={cc([
                        periodChange.isPositive
                          ? "narmi-icon-arrow-up-right"
                          : "narmi-icon-arrow-down-right",
                        styles.periodChangeIcon
                      ])}
                    />
                    <Localized
                      id="dashboard-balance-chart-period-change"
                      vars={{
                        percentage: Math.abs(periodChange.percentage),
                        date: format(periodChange.lastPeriodDate, "MMM d, yyyy")
                      }}
                      elems={{
                        value: <span className={styles.periodChangeValue} />,
                        comparison: <span className={styles.periodChangeText} />
                      }}
                    >
                      <></>
                    </Localized>
                  </div>
                )}
            </div>
          </div>
        </div>
        {staticData && staticData !== "empty" && (
          <DemoChartOverlay staticData={staticData} />
        )}

        {areTabsDisplayed && (
          <div className={styles.timePeriodTabs}>
            <Tabs
              onTabChange={handleTabChange}
              selectedIndex={selectedTab}
            >
              <Tabs.List>
                {visibleTimePeriods.map((timePeriod) => (
                  <Tabs.Tab
                    key={timePeriod}
                    label={timePeriodTabLabel(timePeriod)}
                    tabId={timePeriod}
                  />
                ))}
              </Tabs.List>
            </Tabs>
          </div>
        )}
      </div>
      <BaseChart
        timePeriod={effectiveTimePeriod}
        chartData={chartData}
        windowStart={windowStart}
        windowEnd={windowEnd}
        isLoading={isLoading}
        isSmall={isSmallViewport}
        error={error}
      />
    </div>
  );
};

export default BalanceChart;
